import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PageShell = ({ children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#05050A] text-white" style={{ fontFamily: 'Outfit' }}>
      <header className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-[#05050A]/70 border-b border-white/[0.04]">
        <div className="px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond' }}>PriveNet</button>
          <button onClick={() => navigate('/')} className="text-white/60 hover:text-white text-sm transition-colors">Back to Home</button>
        </div>
      </header>
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 max-w-4xl mx-auto">
        {children}
      </main>
      <footer className="py-8 px-6 md:px-12 lg:px-24 border-t border-white/[0.04]">
        <div className="flex flex-wrap gap-6 justify-center text-sm text-white/40">
          <button onClick={() => navigate('/disclaimer')} className="hover:text-[#C5A059] transition-colors">Disclaimer</button>
          <button onClick={() => navigate('/faq')} className="hover:text-[#C5A059] transition-colors">FAQ</button>
          <button onClick={() => navigate('/legal')} className="hover:text-[#C5A059] transition-colors">Legal & Policies</button>
        </div>
      </footer>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <h2 className="text-2xl lg:text-3xl font-light text-white mb-6 mt-12" style={{ fontFamily: 'Cormorant Garamond' }}>{children}</h2>
);

const SubTitle = ({ children }) => (
  <h3 className="text-lg font-medium text-[#C5A059] mb-3 mt-8" style={{ fontFamily: 'Cormorant Garamond' }}>{children}</h3>
);

const P = ({ children }) => <p className="text-white/60 text-sm leading-relaxed mb-4">{children}</p>;
const Li = ({ children }) => <li className="text-white/60 text-sm leading-relaxed mb-1">{children}</li>;

// ═══════════════════════════════════════════
// DISCLAIMER PAGE
// ═══════════════════════════════════════════

export const DisclaimerPage = () => (
  <PageShell>
    <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-4">Legal</div>
    <h1 className="text-4xl lg:text-5xl font-light text-white mb-12" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="disclaimer-heading">Disclaimer</h1>

    <SectionTitle>Member Disclaimer</SectionTitle>
    <P>PriveNet is a private referral-based network that connects members with independently operating service providers.</P>
    <P>While all professionals are screened through references and onboarding checks, PriveNet does not directly provide the services listed on the platform/network and cannot guarantee outcomes, timelines, or specific results.</P>
    <P>Members are encouraged to independently evaluate services before making financial or legal commitments.</P>
    <P>PriveNet's role is limited to facilitating trusted access, member benefits, and introductions between members and verified professionals.</P>
    <P>Any disputes, payments, or service-specific liabilities remain between the member and the respective service provider.</P>
    <P>PriveNet reserves the right to suspend or remove any vendor or member from the network to maintain quality and trust standards.</P>

    <SectionTitle>Service Provider Disclaimer</SectionTitle>
    <P>PriveNet is a private membership-based referral network designed to connect verified professionals with trusted members.</P>
    <P>All vendors onboarded within the network operate as independent service providers and are solely responsible for the quality, execution, pricing, legality, and delivery of their services.</P>
    <SubTitle>Vendors agree to:</SubTitle>
    <ul className="list-disc ml-6 mb-6">
      <Li>Maintain professional standards and ethical conduct</Li>
      <Li>Offer agreed member benefits or priority access</Li>
      <Li>Transparently report PriveNet member transactions where applicable</Li>
      <Li>Avoid misuse of member information or unauthorized solicitation</Li>
    </ul>
    <P>PriveNet reserves the right to suspend, restrict, or remove any vendor from the network if service quality, trust, or member experience standards are compromised.</P>
  </PageShell>
);

// ═══════════════════════════════════════════
// FAQ PAGE
// ═══════════════════════════════════════════

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.04]">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left" data-testid={`faq-${q.slice(0,20).replace(/\s/g,'-').toLowerCase()}`}>
        <span className="text-white text-sm font-medium pr-4">{q}</span>
        <span className="text-[#C5A059] text-lg flex-shrink-0">{open ? '\u2212' : '+'}</span>
      </button>
      {open && <div className="pb-5"><P>{a}</P></div>}
    </div>
  );
};

export const FAQPage = () => (
  <PageShell>
    <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-4">Help</div>
    <h1 className="text-4xl lg:text-5xl font-light text-white mb-12" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="faq-heading">Frequently Asked Questions</h1>

    <SectionTitle>Member FAQ</SectionTitle>
    <div className="bg-[#0F0F14] border border-white/[0.04] p-6 mb-8" data-testid="member-faq-section">
      <FAQItem q="What is PriveNet?" a="A private, invite-only network that gives members access to trusted and verified professionals across finance, legal, health, home, and lifestyle services." />
      <FAQItem q="How is this different from other platforms?" a="PriveNet focuses on trust and curation, not mass listings. Vendors are onboarded through references and verification." />
      <FAQItem q="How do I access services?" a="Members can request services directly through PriveNet and receive access to verified professionals along with member benefits." />
      <FAQItem q="Are all vendors verified?" a="Yes, every vendor goes through a reference and onboarding process before joining the network." />
      <FAQItem q="What benefits do members get?" a="Depending on the membership plan: Priority access, Member-only pricing/benefits, Trusted referrals, and Exclusive consultations." />
    </div>

    <SectionTitle>Vendor FAQ</SectionTitle>
    <div className="bg-[#0F0F14] border border-white/[0.04] p-6" data-testid="vendor-faq-section">
      <FAQItem q="What kind of clients will I receive?" a="PriveNet focuses on verified, quality-conscious members instead of mass lead generation." />
      <FAQItem q="Is this a marketplace?" a="No. PriveNet is a curated private network, not an open listing platform." />
      <FAQItem q="How does vendor onboarding work?" a="Vendors are selected through references, screening, and quality checks." />
      <FAQItem q="How are member benefits activated?" a="Members may be required to share a valid Member ID and Privy Access Code to receive applicable benefits." />
      <FAQItem q="What is the vendor fee structure?" a="Currently, PriveNet operates with a monthly access fee and/or commission structure depending on category and engagement." />
    </div>
  </PageShell>
);

// ═══════════════════════════════════════════
// LEGAL PAGE
// ═══════════════════════════════════════════

export const LegalPage = () => {
  const [section, setSection] = useState('terms');
  const sections = [
    { id: 'terms', label: 'Terms & Conditions' },
    { id: 'privacy', label: 'Privacy Policy' },
    { id: 'vendor', label: 'Vendor Agreement' },
    { id: 'membership', label: 'Membership Agreement' },
    { id: 'refund', label: 'Refund & Cancellation' },
  ];

  return (
    <PageShell>
      <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-4">Legal</div>
      <h1 className="text-4xl lg:text-5xl font-light text-white mb-8" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="legal-heading">Legal & Policy Documents</h1>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="legal-tabs">
        {sections.map(s => (
          <button key={s.id} data-testid={`legal-tab-${s.id}`} onClick={() => setSection(s.id)}
            className={`px-5 py-2 text-xs font-medium tracking-wide transition-colors ${section === s.id ? 'bg-[#C5A059] text-black' : 'border border-white/20 text-white hover:border-white/60'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {section === 'terms' && <TermsSection />}
      {section === 'privacy' && <PrivacySection />}
      {section === 'vendor' && <VendorSection />}
      {section === 'membership' && <MembershipSection />}
      {section === 'refund' && <RefundSection />}
    </PageShell>
  );
};

const TermsSection = () => (
  <div data-testid="terms-section">
    <SectionTitle>Terms & Conditions</SectionTitle>
    <SubTitle>Introduction</SubTitle>
    <P>Welcome to PriveNet ("PriveNet", "we", "our", or "us"). By accessing, using, or participating in the PriveNet network, website, services, membership programs, or partner ecosystem, you agree to comply with and be bound by these Terms & Conditions.</P>
    <P>PriveNet operates as a private, referral-based membership network connecting members with independently operating service providers.</P>

    <SubTitle>Membership Terms - Eligibility</SubTitle>
    <P>Membership is subject to approval and may be invite-only. PriveNet reserves the right to approve, reject, suspend, or terminate memberships at its discretion.</P>

    <SubTitle>Membership Benefits</SubTitle>
    <P>Members may receive:</P>
    <ul className="list-disc ml-6 mb-4">
      <Li>Access to verified professionals</Li>
      <Li>Priority support or access</Li>
      <Li>Member-only pricing or benefits</Li>
      <Li>Consultation services or concierge assistance</Li>
    </ul>
    <P>Benefits may vary by membership category.</P>

    <SubTitle>Member Responsibilities</SubTitle>
    <P>Members agree:</P>
    <ul className="list-disc ml-6 mb-4">
      <Li>To provide accurate information</Li>
      <Li>Not to misuse the platform or vendor relationships</Li>
      <Li>Not to share member credentials or Privy Access Codes</Li>
      <Li>To conduct independent evaluation before engaging any vendor services</Li>
    </ul>

    <SubTitle>Vendor Terms - Vendor Relationship</SubTitle>
    <P>All vendors listed or introduced through PriveNet are independent service providers. PriveNet does not directly provide legal, medical, financial, home, or professional services unless explicitly stated.</P>

    <SubTitle>Vendor Responsibilities</SubTitle>
    <P>Vendors agree to:</P>
    <ul className="list-disc ml-6 mb-4">
      <Li>Maintain professional standards</Li>
      <Li>Deliver services ethically and lawfully</Li>
      <Li>Provide agreed member benefits</Li>
      <Li>Maintain confidentiality of member information</Li>
      <Li>Report applicable PriveNet member transactions when required</Li>
    </ul>
    <P>PriveNet reserves the right to remove vendors from the network at any time.</P>

    <SubTitle>Payments & Fees</SubTitle>
    <P>Membership fees are billed as per the selected plan and may be revised periodically. Vendor subscription fees and commission structures are governed through separate vendor agreements.</P>
    <P>Unless explicitly stated otherwise: Membership fees are non-refundable. Vendor onboarding fees are non-refundable. Refund requests are evaluated at PriveNet's discretion.</P>

    <SubTitle>Limitation of Liability</SubTitle>
    <P>PriveNet acts solely as a referral and network facilitation platform. We do not guarantee specific outcomes, vendor availability, service quality consistency, or timelines/delivery standards.</P>
    <P>Any service engagement, payment, dispute, or liability remains between the member and the respective service provider.</P>
    <P>PriveNet shall not be liable for indirect losses, financial losses, personal disputes, professional negligence by vendors, or delays/service interruptions.</P>

    <SubTitle>Intellectual Property</SubTitle>
    <P>All branding, logos, content, systems, designs, and materials associated with PriveNet are the intellectual property of PriveNet and may not be copied or reproduced without written permission.</P>

    <SubTitle>Termination</SubTitle>
    <P>PriveNet reserves the right to suspend or terminate any member or vendor account that violates policies, misuses the network, harms brand reputation, or engages in unethical conduct.</P>

    <SubTitle>Governing Law</SubTitle>
    <P>These terms shall be governed by the laws of India. Any disputes shall be subject to the jurisdiction of Gurugram, Haryana.</P>
  </div>
);

const PrivacySection = () => (
  <div data-testid="privacy-section">
    <SectionTitle>Privacy Policy</SectionTitle>
    <SubTitle>Introduction</SubTitle>
    <P>PriveNet respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard information shared with us.</P>

    <SubTitle>Information We Collect</SubTitle>
    <P>We may collect:</P>
    <ul className="list-disc ml-6 mb-4">
      <Li>Name, Phone number, Email address</Li>
      <Li>Membership information</Li>
      <Li>Service preferences</Li>
      <Li>Vendor details</Li>
      <Li>Communication records</Li>
    </ul>

    <SubTitle>How We Use Information</SubTitle>
    <P>We use information to:</P>
    <ul className="list-disc ml-6 mb-4">
      <Li>Verify members and vendors</Li>
      <Li>Facilitate service introductions</Li>
      <Li>Improve member experience</Li>
      <Li>Communicate updates and offers</Li>
      <Li>Manage memberships and support</Li>
    </ul>

    <SubTitle>Information Sharing</SubTitle>
    <P>We do not sell personal data. Information may be shared only with relevant vendors for service facilitation, when legally required, or for operational support purposes.</P>

    <SubTitle>Data Security</SubTitle>
    <P>We take reasonable steps to protect user information. However, no digital system can guarantee complete security.</P>

    <SubTitle>User Rights</SubTitle>
    <P>Users may request access to stored information, correction of inaccurate information, or removal from communication lists.</P>
  </div>
);

const VendorSection = () => (
  <div data-testid="vendor-section">
    <SectionTitle>Vendor Agreement</SectionTitle>
    <SubTitle>This Agreement</SubTitle>
    <P>This agreement is entered into between PriveNet and the Vendor ("Service Provider").</P>

    <SubTitle>Scope</SubTitle>
    <P>The Vendor agrees to provide professional services to PriveNet members subject to mutually agreed terms.</P>

    <SubTitle>Vendor Obligations</SubTitle>
    <P>The Vendor agrees to:</P>
    <ul className="list-disc ml-6 mb-4">
      <Li>Maintain service quality</Li>
      <Li>Respond professionally and promptly</Li>
      <Li>Offer agreed member benefits</Li>
      <Li>Maintain confidentiality</Li>
      <Li>Avoid misleading practices</Li>
    </ul>

    <SubTitle>Fees</SubTitle>
    <P>Vendor participation may involve monthly subscription fee, commission-based model, or promotional partnership arrangements. Details may vary by category.</P>

    <SubTitle>Non-Circumvention</SubTitle>
    <P>The Vendor agrees not to misuse PriveNet member data or misrepresent association with PriveNet.</P>

    <SubTitle>Independent Relationship</SubTitle>
    <P>The Vendor operates independently and is solely responsible for service delivery.</P>

    <SubTitle>Termination</SubTitle>
    <P>Either party may terminate participation with written notice. PriveNet reserves the right to terminate immediately in case of misconduct, quality concerns, or policy violations.</P>
  </div>
);

const MembershipSection = () => (
  <div data-testid="membership-section">
    <SectionTitle>Membership Agreement</SectionTitle>
    <SubTitle>Member Participation</SubTitle>
    <P>By joining PriveNet, members acknowledge that:</P>
    <ul className="list-disc ml-6 mb-4">
      <Li>PriveNet is a referral and access network</Li>
      <Li>Vendors operate independently</Li>
      <Li>Benefits may vary by membership category</Li>
      <Li>Membership may be revoked for misuse</Li>
    </ul>

    <SubTitle>Membership Benefits</SubTitle>
    <P>Benefits may include:</P>
    <ul className="list-disc ml-6 mb-4">
      <Li>Priority access</Li>
      <Li>Exclusive pricing</Li>
      <Li>Trusted vendor introductions</Li>
      <Li>Concierge assistance</Li>
    </ul>

    <SubTitle>Usage Rules</SubTitle>
    <P>Members agree:</P>
    <ul className="list-disc ml-6 mb-4">
      <Li>Not to misuse vendor information</Li>
      <Li>Not to share access credentials</Li>
      <Li>To maintain respectful communication</Li>
    </ul>
  </div>
);

const RefundSection = () => (
  <div data-testid="refund-section">
    <SectionTitle>Refund & Cancellation Policy</SectionTitle>
    <SubTitle>Membership Cancellation</SubTitle>
    <P>Members may request cancellation anytime. Unless otherwise specified: Membership fees are non-refundable. Benefits cease upon expiry or cancellation.</P>

    <SubTitle>Vendor Cancellation</SubTitle>
    <P>Vendors may discontinue participation with written notice. Outstanding dues, commissions, or obligations must be settled before exit.</P>

    <SubTitle>Exceptional Cases</SubTitle>
    <P>Refunds, if any, shall be solely at PriveNet's discretion.</P>
  </div>
);
